'use client'

import { extractMessages } from '@/ApiClient/api.utils'
import { showToast } from '@/components/Toast/ToastManager'
import {
  SESSION_CLOCK_SKEW_MS,
  SESSION_TOKEN_STORAGE_KEY,
  emitSessionExpired,
  setSessionCookie,
} from '@/lib/sessionLifecycle'
import { FilesService } from '@/services/files/files.services'
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios'
import { get, set, some, unset } from 'lodash-es'
import { getSession } from 'next-auth/react'
import { FiXCircle } from 'react-icons/fi'

const REFRESH_THRESHOLD_MS = 15 * 60 * 1000
const SESSION_TOKEN_CACHE_MS = 1000

let refreshPromise: Promise<string | null> | null = null
let sessionTokenCache: { token: string | null; expiresAt: number } | null = null

declare module 'axios' {
  export interface AxiosRequestConfig {
    paginated?: boolean
    includeHeaders?: boolean
  }
}
// Define paginated response type
export interface PaginatedResponse<T> {
  data: T[]
  meta: {
    page: number
    take: number
    itemCount: number
    pageCount: number
    hasPreviousPage: boolean
    hasNextPage: boolean
  }
}

// Enhanced CustomAxiosInstance
type CustomAxiosInstance = Omit<AxiosInstance, 'get' | 'post' | 'put' | 'delete'> & {
  get<T = unknown>(
    url: string,
    config?: AxiosRequestConfig & { paginated?: boolean; includeHeaders?: boolean }
  ): Promise<T extends any[] ? PaginatedResponse<T[number]> : T>
  post<T = unknown>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>
  put<T = unknown>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>
  delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T>
}

// Create the Axios client
const apiClient: CustomAxiosInstance = axios.create({
  baseURL: process.env.NEXT_PUBLIC_GATEWAY_URL,
})

function parseJwtExpiration(token?: string | null) {
  if (!token) return null

  try {
    const [, payload] = token.split('.')
    if (!payload) return null

    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4)
    const decoded = JSON.parse(atob(padded))

    return typeof decoded?.exp === 'number' ? decoded.exp * 1000 - SESSION_CLOCK_SKEW_MS : null
  } catch {
    return null
  }
}

function getStoredToken() {
  if (typeof window === 'undefined') return null

  try {
    return localStorage.getItem(SESSION_TOKEN_STORAGE_KEY)
  } catch {
    return null
  }
}

function chooseLatestToken(sessionToken?: string | null, storedToken?: string | null) {
  const sessionExpiry = parseJwtExpiration(sessionToken)
  const storedExpiry = parseJwtExpiration(storedToken)

  if (!storedToken) return sessionToken ?? null
  if (!sessionToken) return storedToken
  if (!storedExpiry) return sessionToken
  if (!sessionExpiry) return storedToken

  return storedExpiry >= sessionExpiry ? storedToken : sessionToken
}

async function getCachedSessionToken() {
  const now = Date.now()

  if (sessionTokenCache && sessionTokenCache.expiresAt > now) {
    return sessionTokenCache.token
  }

  const session = await getSession()
  const token =
    session?.accessToken ??
    (session as any)?.user?.accessToken ??
    (session as any)?.user?.access_token ??
    null

  sessionTokenCache = {
    token,
    expiresAt: now + SESSION_TOKEN_CACHE_MS,
  }

  return token
}

function shouldAttemptRefresh(token?: string | null, url?: string) {
  if (!token) return false
  if (!url) return true

  const skipRefresh =
    url.includes('/auth/login') ||
    url.includes('/auth/federatedLogin') ||
    url.includes('/auth/refresh') ||
    url.includes('/users/logout')

  if (skipRefresh) return false

  const expiresAt = parseJwtExpiration(token)
  if (!expiresAt) return false

  return expiresAt - Date.now() <= REFRESH_THRESHOLD_MS
}

async function refreshAccessToken(currentToken: string): Promise<string | null> {
  try {
    const response = await fetch(`${process.env.NEXT_PUBLIC_GATEWAY_URL}/auth/refresh`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${currentToken}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })

    if (!response.ok) {
      throw new Error(`Refresh failed with status ${response.status}`)
    }

    const data = (await response.json()) as {
      accessToken?: string
      access_token?: string
      expiresIn?: number
    }

    const nextToken = data.accessToken ?? data.access_token
    const nextExpiresIn = Number(data.expiresIn ?? 0)

    if (!nextToken || !nextExpiresIn) {
      throw new Error('Refresh response missing token')
    }

    setSessionCookie(nextToken, nextExpiresIn)
    sessionTokenCache = {
      token: nextToken,
      expiresAt: Date.now() + SESSION_TOKEN_CACHE_MS,
    }
    return nextToken
  } catch {
    emitSessionExpired('refresh-failed')
    return null
  }
}

async function getRefreshedToken(currentToken: string) {
  if (!refreshPromise) {
    refreshPromise = refreshAccessToken(currentToken).finally(() => {
      refreshPromise = null
    })
  }

  return refreshPromise
}

/**
 * Carica i file in sequenza e restituisce un oggetto con gli id dei file caricati.
 *
 * @param files - Oggetto con chiavi (nome) e File da caricare
 * @returns Oggetto con chiavi originali e id dei file caricati
 */
async function uploadFiles(
  files: Record<string, File>,
  processStateId: string
): Promise<Record<string, any>> {
  const uploaded: Record<string, any> = {}

  for (const key of Object.keys(files)) {
    const file = files[key]

    if (!file) continue
    if (file instanceof File) {
      const data = await FilesService.uploadFile(file, processStateId)
      uploaded[key] = data.id // Adatta se la risposta è diversa
    } else if (typeof file === 'number') {
      uploaded[key] = file
    }
  }

  return uploaded
}

// Interceptor per gestire upload file prima della richiesta principale
apiClient.interceptors.request.use(
  async config => {
    const storedToken = getStoredToken()
    let token = storedToken

    if (!token) {
      token = await getCachedSessionToken()
    }

    if (shouldAttemptRefresh(token, config.url)) {
      token = await getRefreshedToken(token as string)
    }

    if (token) {
      config.headers = config.headers ?? {}
      config.headers.Authorization = `Bearer ${token}`
    }

    // Salta l'upload dei file per endpoint che non gestiscono dati di stato (es. change-state, reject, ecc.)
    const url = config.url || ''
    const skipFileUpload =
      url.includes('/change-state') || url.includes('/reject') || !config?.data?.current_state_id

    if (!skipFileUpload) {
      const getProp = 'data.data.' + config?.data?.current_state_id + '.files'
      //console.log('🚀 ~ prop:', getProp)
      const filesObject = get(config, getProp)
      // Verifica se hasFiles ha proprietà non undefined usando lodash
      const hasFiles = filesObject && some(filesObject, file => file !== undefined && file !== null)
      // Gestione upload files
      if (hasFiles) {
        const updateProp = 'data.data.' + config?.data?.current_state_id
        console.log('🚀 ~ Upload files before request:', {
          url: config.url,
          updateProp,
          filesCount: Object.keys(filesObject).length,
        })
        // L'upload DEVE completarsi prima di procedere con la richiesta principale
        // Se l'upload fallisce, blocchiamo la richiesta principale (l'errore verrà propagato)
        const uploadedIds = await uploadFiles(filesObject, config?.data?.current_state_id)
        console.log('🚀 ~ Files uploaded successfully:', uploadedIds)
        // Notifica globale: i file del current_state_id sono stati caricati, con i rispettivi ID
        try {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(
              new CustomEvent('files:uploaded', {
                detail: {
                  stateId: config?.data?.current_state_id,
                  ids: uploadedIds,
                  updateProp: getProp,
                  url: config.url,
                },
              })
            )
          }
        } catch (e) {
          console.warn('files:uploaded event dispatch failed', e)
        }
        set(config, getProp, uploadedIds)
      } else {
        unset(config, getProp)
      }
    }

    return config
  },
  error => Promise.reject(error)
)

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  response => {
    // Se la chiamata richiede anche gli header, restituisci data + headers indipendentemente dal responseType
    if (response.config.includeHeaders) {
      return { data: response.data, headers: response.headers }
    }

    // Se la risposta è di tipo arraybuffer o blob, restituisci direttamente i dati
    if (response.config.responseType === 'arraybuffer' || response.config.responseType === 'blob') {
      return response.data
    }

    // Altrimenti restituisci i dati come prima
    return response?.data
  },
  error => {
    if (error.response?.status === 401) {
      const requestUrl = String(error.config?.url || '')
      const skipSessionExpiryHandling =
        requestUrl.includes('/auth/login') ||
        requestUrl.includes('/auth/federatedLogin') ||
        requestUrl.includes('/auth/refresh') ||
        requestUrl.includes('/users/logout')

      if (!skipSessionExpiryHandling) {
        emitSessionExpired('unauthorized')
      }

      return Promise.reject(error)
    }

    // Gestione speciale per errori con risposta di tipo arraybuffer
    if (error.config?.responseType === 'arraybuffer' && error.response?.data) {
      try {
        // Prova a decodificare il messaggio di errore dal buffer
        const decoder = new TextDecoder('utf-8')
        const errorText = decoder.decode(error.response.data)
        let errorMessage = 'Errore sconosciuto'

        try {
          // Prova a parsare come JSON
          const errorJson = JSON.parse(errorText)
          errorMessage = errorJson.message || errorText
        } catch {
          // Se non è JSON, usa il testo grezzo
          errorMessage = errorText
        }

        showToast({
          message: 'ERRORE',
          description: errorMessage,
          type: 'error',
          icon: FiXCircle,
          toastId: 'error',
        })
      } catch (e) {
        // Se non riesci a decodificare, mostra un messaggio generico
        showToast({
          message: 'ERRORE',
          description: 'Si è verificato un errore generico',
          type: 'error',
          icon: FiXCircle,
        })
      }
    } else {
      // Gestione standard degli errori
      const errorData = error.response?.data as ErrorResponse
      if (errorData?.message && Array.isArray(errorData.message)) {
        const messages = extractMessages(errorData.message).join('\n')

        showToast({
          message: `Errore ${errorData.statusCode}`,
          description: messages || 'Errore di validazione',
          type: 'error',
          icon: FiXCircle,
        })
      } else {
        const errorMessage =
          error.response?.data?.message || 'Si è verificato un errore inaspettato'
        showToast({
          message: 'ERRORE',
          description: errorMessage,
          type: 'error',
          icon: FiXCircle,
        })
      }
    }
    return Promise.reject(error)
  }
)

interface ErrorResponse {
  message: Message[]
  error: string
  statusCode: number
}

interface Message {
  target: Target
  value: string
  property: string
  constraints: Constraints
}

interface Constraints {
  minLength: string
}

interface Target {
  email: string
  password: string
  name: string
  surname: string
  fiscal_code: string
}

export default apiClient
